import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IconDirective } from '@coreui/icons-angular';
import { filter, Subscription } from 'rxjs';
import { schedulePlotWorkspaceDomCleanup } from '../plot-workspace-dom-cleanup.util';
import { AppNavBackService, AppNavBackTarget } from './app-nav-back.service';

@Component({
  selector: 'app-nav-back',
  standalone: true,
  imports: [CommonModule, IconDirective],
  templateUrl: './app-nav-back.component.html',
  styleUrl: './app-nav-back.component.scss',
})
export class AppNavBackComponent implements OnInit, OnDestroy {
  /** Override automatic parent resolution */
  @Input() backCommands: (string | number)[] | null = null;
  @Input() backQueryParams: Record<string, string | number | boolean | null | undefined> | null =
    null;
  @Input() backLabel: string | null = null;
  /** Compact: icon only on narrow screens */
  @Input() compact = false;

  target: AppNavBackTarget | null = null;
  private routeSub?: Subscription;

  constructor(
    private readonly navBack: AppNavBackService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.refreshTarget();
    this.routeSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.refreshTarget());
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  get visible(): boolean {
    if (this.backCommands?.length) {
      return true;
    }
    return !!this.target;
  }

  get label(): string {
    if (this.backLabel) {
      return this.backLabel;
    }
    return this.target?.label || '上一層';
  }

  /** 僅顯示返回圖示，不顯示「上一層」文字 */
  get showLabel(): boolean {
    return false;
  }

  get ariaLabel(): string {
    const cmds = this.effectiveCommands;
    if (cmds?.length && this.navBack.isImaginusHomeCommands(cmds)) {
      return '返回 IMA One 首頁';
    }
    return '返回' + this.label;
  }

  private get effectiveCommands(): (string | number)[] | null {
    if (this.backCommands?.length) {
      return this.backCommands;
    }
    return this.target?.commands ?? null;
  }

  onBackClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const leavingPlotWorkspace = /\/plot-(?:view|edit)\//.test(this.router.url || '');
    if (leavingPlotWorkspace) {
      schedulePlotWorkspaceDomCleanup({ forcePlotLayers: true });
    }
    if (this.backCommands?.length) {
      void this.router.navigate(this.backCommands, {
        queryParams: this.backQueryParams || undefined,
      });
      return;
    }
    this.navBack.navigateBack();
  }

  private refreshTarget(): void {
    if (this.backCommands?.length) {
      this.target = {
        commands: this.backCommands,
        queryParams: this.backQueryParams || undefined,
        label: this.backLabel || '上一層',
      };
      return;
    }
    this.target = this.navBack.resolveBackTarget();
  }
}
